<template>
  <div class="cash-page">
    <div class="cash-shell">
      <div class="cash-header">
        <div>
          <h2 class="cash-title">Para &amp; Bankalar</h2>
          <p class="cash-subtitle">Altında Fırsat nakit hesapları ve kasaları</p>
        </div>
        <div class="cash-header-actions">
          <div v-if="companyName" class="cash-company-filter">
            <span class="cash-company-label">Şirket</span>
            <span class="cash-chip cash-chip-blue">{{ companyName }}</span>
          </div>
          <button type="button" class="cash-btn cash-btn-ghost" :disabled="loading" @click="loadAccounts">
            <v-icon small class="mr-1">refresh</v-icon>
            Yenile
          </button>
        </div>
      </div>

      <div class="cash-summary">
        <div class="cash-summary-card cash-summary-wide">
          <div class="cash-summary-head">
            <span class="cash-summary-label">Toplam Bakiye</span>
            <div class="cash-summary-icon cash-icon-primary"><v-icon small>account_balance_wallet</v-icon></div>
          </div>
          <div v-if="currencyTotals.length" class="cash-summary-chips">
            <span
              v-for="ct in currencyTotals"
              :key="ct.currencyId"
              class="cash-summary-chip"
              :class="{ 'cash-negative': ct.total < 0 }"
            >{{ formatMoneyCur(ct.total, ct.symbol || ct.code) }}</span>
          </div>
          <div v-else class="cash-summary-empty">Bakiye yok</div>
        </div>
        <div class="cash-summary-card">
          <div class="cash-summary-head">
            <span class="cash-summary-label">Banka</span>
            <div class="cash-summary-icon cash-icon-blue"><v-icon small>account_balance</v-icon></div>
          </div>
          <div v-if="bankTotals.length" class="cash-summary-chips">
            <span
              v-for="ct in bankTotals"
              :key="ct.currencyId"
              class="cash-summary-chip"
              :class="{ 'cash-negative': ct.total < 0 }"
            >{{ formatMoneyCur(ct.total, ct.symbol || ct.code) }}</span>
          </div>
          <div v-else class="cash-summary-empty">Bakiye yok</div>
        </div>
        <div class="cash-summary-card">
          <div class="cash-summary-head">
            <span class="cash-summary-label">Kasa</span>
            <div class="cash-summary-icon cash-icon-amber"><v-icon small>savings</v-icon></div>
          </div>
          <div v-if="vaultTotals.length" class="cash-summary-chips">
            <span
              v-for="ct in vaultTotals"
              :key="ct.currencyId"
              class="cash-summary-chip"
              :class="{ 'cash-negative': ct.total < 0 }"
            >{{ formatMoneyCur(ct.total, ct.symbol || ct.code) }}</span>
          </div>
          <div v-else class="cash-summary-empty">Bakiye yok</div>
        </div>
      </div>

      <div v-if="loading" class="cash-state">
        <v-progress-circular indeterminate color="primary" size="34"></v-progress-circular>
        <span>Yükleniyor...</span>
      </div>

      <div v-else-if="!accounts.length" class="cash-state cash-empty">
        <v-icon size="42" color="#cbd5e1">account_balance</v-icon>
        <span>Henüz hesap yok</span>
      </div>

      <div v-else class="cash-grid">
        <div
          v-for="acc in accounts"
          :key="acc.id"
          class="cash-card"
          :class="{ 'cash-card-vault': acc.accountType === 1, 'cash-card-passive': acc.status === 'PASSIVE' }"
        >
          <div class="cash-card-top">
            <div class="cash-card-badge" :class="acc.accountType === 1 ? 'cash-badge-vault' : 'cash-badge-bank'">
              <v-icon x-small class="mr-1">{{ acc.accountType === 1 ? 'savings' : 'account_balance' }}</v-icon>
              {{ acc.accountType === 1 ? 'Kasa' : 'Banka' }}
            </div>
            <div class="cash-card-menu">
              <span v-if="acc.companyName" class="cash-company-chip">{{ acc.companyName }}</span>
              <span v-if="acc.status === 'PASSIVE'" class="cash-pill-passive">Pasif</span>
            </div>
          </div>

          <div class="cash-card-name">{{ acc.name }}</div>
          <div v-if="acc.iban" class="cash-card-iban">{{ acc.iban }}</div>
          <div class="cash-card-balances">
            <template v-if="acc.balances && acc.balances.length">
              <div
                v-for="bal in acc.balances"
                :key="bal.currencyId"
                class="cash-card-balance-line"
              >
                <span class="cash-bal-code">{{ bal.code }}</span>
                <span class="cash-bal-amount" :class="{ 'cash-negative': Number(bal.balance) < 0 }">{{ formatMoneyCur(bal.balance, bal.symbol || bal.code) }}</span>
              </div>
            </template>
            <div v-else class="cash-card-balance-empty">Bakiye yok</div>
          </div>

          <div class="cash-card-actions">
            <button type="button" class="cash-op-btn cash-op-income" @click="openIncomeDialog(acc)">
              <v-icon x-small class="mr-1">south_west</v-icon> Para Girdisi
            </button>
            <button type="button" class="cash-op-btn cash-op-payment" @click="openPaymentDialog(acc)">
              <v-icon x-small class="mr-1">north_east</v-icon> Diğer Ödeme
            </button>
            <button type="button" class="cash-op-btn cash-op-transfer" @click="openTransferDialog(acc)">
              <v-icon x-small class="mr-1">swap_horiz</v-icon> Transfer
            </button>
          </div>
        </div>
      </div>

      <div class="cash-history">
        <div class="cash-history-header">
          <div>
            <h3 class="cash-history-title">Hareket Geçmişi</h3>
            <p class="cash-subtitle">{{ formatDate(historyDate) }} tarihli kayıtlar</p>
          </div>
          <div class="cash-header-actions">
            <v-menu v-model="historyDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="290px">
              <template v-slot:activator="{ on }">
                <button type="button" class="cash-btn cash-btn-ghost" v-on="on">
                  <v-icon small class="mr-1">event</v-icon>
                  {{ formatDate(historyDate) }}
                </button>
              </template>
              <v-date-picker v-model="historyDate" locale="tr-TR" @input="historyDateMenu = false; loadHistory()"></v-date-picker>
            </v-menu>
            <button type="button" class="cash-btn cash-btn-primary" :disabled="historyLoading" @click="loadHistory">
              <v-icon small class="mr-1">refresh</v-icon>
              Yenile
            </button>
          </div>
        </div>

        <v-tabs v-model="historyTab" class="cash-history-tabs" color="primary" slider-color="primary" @change="onHistoryTabChanged">
          <v-tab>Tümü</v-tab>
          <v-tab>Para Girdileri</v-tab>
          <v-tab>Diğer Ödemeler</v-tab>
          <v-tab>Transferler</v-tab>
          <v-tab>Düzeltmeler</v-tab>
        </v-tabs>

        <div v-if="historyTab === 2" class="cash-history-filter">
          <span class="cash-company-label">Ödeme Tipi</span>
          <v-select
            v-model="historyPaymentType"
            :items="historyPaymentTypeOptions"
            hide-details
            dense
            outlined
            class="cash-history-filter-select"
            @change="historyPage = 1"
          ></v-select>
        </div>

        <div class="cash-table-wrap">
          <div class="cash-table-scroll">
            <table class="cash-table">
              <thead>
                <tr>
                  <th>Tarih</th>
                  <th>Hesap(lar)</th>
                  <th>Tür</th>
                  <th>Tutar</th>
                  <th>Bakiye (Sonrası)</th>
                  <th>Yapan</th>
                  <th>Açıklama</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="historyLoading">
                  <td colspan="7" class="cash-cell-state">
                    <v-progress-circular indeterminate color="primary" size="30"></v-progress-circular>
                    Yükleniyor...
                  </td>
                </tr>
                <tr v-else-if="!paginatedHistory.length">
                  <td colspan="7" class="cash-cell-state">Kayıt yok</td>
                </tr>
                <tr v-for="(row, i) in paginatedHistory" :key="row.id || i" class="cash-row">
                  <td class="cash-cell-muted">{{ formatDateTime(row.createdAt) }}</td>
                  <td class="cash-cell-bold">{{ historyAccountLabel(row) }}</td>
                  <td>
                    <span class="cash-chip" :class="historyChipClass(row.subType)">{{ historyTypeLabel(row.subType) }}</span>
                    <span v-if="historyCurrency(row)" class="cash-cur-chip">{{ historyCurrency(row) }}</span>
                  </td>
                  <td class="cash-cell-amount" :class="historyAmountClass(row.subType)">{{ historyAmount(row) }}</td>
                  <td class="cash-cell-value">{{ historyAfterBalance(row) }}</td>
                  <td class="cash-cell-muted">{{ row.userName || '-' }}</td>
                  <td class="cash-cell-desc">{{ historyDescription(row) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="cash-table-footer">
            <div class="cash-footer-left">
              <span>Sayfa başına satır:</span>
              <select v-model.number="historyRowsPerPage" class="cash-rows-select">
                <option :value="10">10</option>
                <option :value="20">20</option>
                <option :value="50">50</option>
              </select>
            </div>
            <span class="cash-pagination-info">{{ historyPaginationInfo }}</span>
            <div class="cash-footer-actions">
              <button type="button" class="cash-page-btn" :disabled="historyPage <= 1" @click="historyPage = Math.max(1, historyPage - 1)">
                <v-icon small>chevron_left</v-icon>
              </button>
              <button type="button" class="cash-page-btn" :disabled="historyPage >= historyTotalPages" @click="historyPage = Math.min(historyTotalPages, historyPage + 1)">
                <v-icon small>chevron_right</v-icon>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="cash-history cash-snapshot-section">
        <div class="cash-history-header">
          <div>
            <h3 class="cash-history-title">Snapshot Geçmişi</h3>
            <p class="cash-subtitle">Günlük bakiye snapshot kayıtları</p>
          </div>
          <div class="cash-header-actions">
            <v-menu v-model="snapStartMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="290px">
              <template v-slot:activator="{ on }">
                <button type="button" class="cash-btn cash-btn-ghost" v-on="on">
                  <v-icon small class="mr-1">event</v-icon>
                  Başlangıç: {{ snapStartDate ? formatDate(snapStartDate) : '-' }}
                </button>
              </template>
              <v-date-picker v-model="snapStartDate" locale="tr-TR" @input="snapStartMenu = false"></v-date-picker>
            </v-menu>
            <v-menu v-model="snapEndMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="290px">
              <template v-slot:activator="{ on }">
                <button type="button" class="cash-btn cash-btn-ghost" v-on="on">
                  <v-icon small class="mr-1">event</v-icon>
                  Bitiş: {{ snapEndDate ? formatDate(snapEndDate) : '-' }}
                </button>
              </template>
              <v-date-picker v-model="snapEndDate" locale="tr-TR" @input="snapEndMenu = false"></v-date-picker>
            </v-menu>
            <button type="button" class="cash-btn cash-btn-primary" :disabled="snapshotsLoading" @click="loadSnapshots">
              <v-icon small class="mr-1">refresh</v-icon>
              Listele
            </button>
            <button type="button" class="cash-btn cash-btn-success" :disabled="snapshotsLoading" @click="exportSnapshotsExcel">
              <v-icon small class="mr-1">download</v-icon>
              Excel İndir (Sekmeli)
            </button>
          </div>
        </div>

        <div class="cash-table-wrap">
          <div class="cash-table-scroll">
            <table class="cash-table">
              <thead>
                <tr>
                  <th>Tarih</th>
                  <th>Toplamlar</th>
                  <th>Hesap Sayısı</th>
                  <th class="cash-snap-th-detail">Detay</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="snapshotsLoading">
                  <td colspan="4" class="cash-cell-state">
                    <v-progress-circular indeterminate color="primary" size="30"></v-progress-circular>
                    Yükleniyor...
                  </td>
                </tr>
                <tr v-else-if="!paginatedSnapshots.length">
                  <td colspan="4" class="cash-cell-state">Kayıt yok</td>
                </tr>
                <template v-for="(item, i) in paginatedSnapshots">
                  <tr :key="'snap-' + (item.id || i)" class="cash-row">
                    <td class="cash-cell-bold">{{ formatDateTime(item.createdAt) }}</td>
                    <td>
                      <div v-if="snapshotTotals(item).length" class="cash-snap-chips">
                        <span v-for="(t, ti) in snapshotTotals(item)" :key="ti" class="cash-cur-chip">
                          {{ formatMoneyCur(snapPick(t, 'Total', 'total'), snapPick(t, 'Symbol', 'symbol') || snapPick(t, 'Code', 'code')) }}
                        </span>
                      </div>
                      <span v-else class="cash-cell-muted">-</span>
                    </td>
                    <td class="cash-cell-value">{{ snapshotAccounts(item).length }}</td>
                    <td class="cash-snap-cell-detail">
                      <button type="button" class="cash-op-btn cash-op-transfer cash-snap-detail-btn" @click="toggleSnapshotDetail(i)">
                        {{ expandedSnapshot === i ? 'Gizle' : 'Detay' }}
                      </button>
                    </td>
                  </tr>
                  <tr v-if="expandedSnapshot === i" :key="'snap-detail-' + (item.id || i)" class="cash-snap-detail-row">
                    <td colspan="4">
                      <div class="cash-snap-detail-wrap">
                        <div v-if="!snapshotAccounts(item).length" class="cash-cell-muted">Detay bulunamadı.</div>
                        <div v-for="(acc, ai) in snapshotAccounts(item)" :key="ai" class="cash-snap-detail-card">
                          <div class="cash-snap-detail-head">
                            <span class="cash-snap-detail-name">{{ snapPick(acc, 'Name', 'name') }}</span>
                            <span class="cash-chip" :class="Number(snapPick(acc, 'AccountType', 'accountType')) === 1 ? 'cash-chip-slate' : 'cash-chip-blue'">
                              {{ Number(snapPick(acc, 'AccountType', 'accountType')) === 1 ? 'Kasa' : 'Banka' }}
                            </span>
                            <span class="cash-chip" :class="snapPick(acc, 'Status', 'status') === 'ACTIVE' ? 'cash-chip-green' : 'cash-chip-grey'">
                              {{ snapPick(acc, 'Status', 'status') === 'ACTIVE' ? 'Aktif' : 'Pasif' }}
                            </span>
                          </div>
                          <div class="cash-snap-balances">
                            <span
                              v-for="(b, bi) in snapshotAccountBalances(acc)"
                              :key="bi"
                              class="cash-cur-chip"
                              :class="{ 'cash-negative': Number(snapPick(b, 'Balance', 'balance')) < 0 }"
                            >{{ formatMoneyCur(snapPick(b, 'Balance', 'balance'), snapPick(b, 'Symbol', 'symbol') || snapPick(b, 'Code', 'code')) }}</span>
                            <span v-if="!snapshotAccountBalances(acc).length" class="cash-cell-muted">-</span>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
          <div class="cash-table-footer">
            <div class="cash-footer-left">
              <span>Sayfa başına satır:</span>
              <select v-model.number="snapshotsRowsPerPage" class="cash-rows-select">
                <option :value="10">10</option>
                <option :value="20">20</option>
                <option :value="50">50</option>
              </select>
            </div>
            <span class="cash-pagination-info">{{ snapshotsPaginationInfo }}</span>
            <div class="cash-footer-actions">
              <button type="button" class="cash-page-btn" :disabled="snapshotsPage <= 1" @click="snapshotsPage = Math.max(1, snapshotsPage - 1)">
                <v-icon small>chevron_left</v-icon>
              </button>
              <button type="button" class="cash-page-btn" :disabled="snapshotsPage >= snapshotsTotalPages" @click="snapshotsPage = Math.min(snapshotsTotalPages, snapshotsPage + 1)">
                <v-icon small>chevron_right</v-icon>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <v-dialog v-model="incomeDialog" max-width="520px" persistent>
      <v-card class="cash-modal">
        <v-card-title class="cash-modal-head cash-modal-head-green">
          <v-icon left color="white">south_west</v-icon>
          <span class="font-weight-bold">Para Girdisi</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="incomeDialog = false" :disabled="incomeSaving"><v-icon>close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pa-4">
          <div class="cash-target">{{ selectedAccount ? selectedAccount.name : '' }}</div>
          <p v-if="!currencyOptions.length" class="cash-currency-hint">Önce Admin &gt; Para Birimleri bölümünden para birimi ekleyin.</p>
          <v-container grid-list-md class="pa-0">
            <v-layout wrap>
              <v-flex xs12>
                <v-select v-model="incomeForm.currencyId" :items="currencyOptions" label="Para Birimi" outlined dense></v-select>
              </v-flex>
              <v-flex xs12>
                <v-text-field v-model.number="incomeForm.amount" label="Tutar" type="number" step="0.01" min="0" outlined dense></v-text-field>
              </v-flex>
              <v-flex xs12>
                <v-menu v-model="incomeDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="290px">
                  <template v-slot:activator="{ on }">
                    <v-text-field :value="incomeForm.date ? formatDate(incomeForm.date) : ''" label="Tarih (isteğe bağlı)" outlined dense readonly clearable prepend-inner-icon="event" v-on="on" @click:clear="incomeForm.date = null"></v-text-field>
                  </template>
                  <v-date-picker v-model="incomeForm.date" locale="tr-TR" @input="incomeDateMenu = false"></v-date-picker>
                </v-menu>
              </v-flex>
              <v-flex xs12>
                <v-textarea v-model="incomeForm.description" label="Açıklama" rows="2" outlined dense></v-textarea>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="incomeDialog = false" :disabled="incomeSaving">İptal</v-btn>
          <v-btn color="success" depressed class="white--text" :loading="incomeSaving" @click="submitIncome">Kaydet</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="paymentDialog" max-width="520px" persistent>
      <v-card class="cash-modal">
        <v-card-title class="cash-modal-head cash-modal-head-indigo">
          <v-icon left color="white">north_east</v-icon>
          <span class="font-weight-bold">Diğer Ödeme</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="paymentDialog = false" :disabled="paymentSaving"><v-icon>close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pa-4">
          <div class="cash-target">{{ selectedAccount ? selectedAccount.name : '' }}</div>
          <p v-if="!currencyOptions.length" class="cash-currency-hint">Önce Admin &gt; Para Birimleri bölümünden para birimi ekleyin.</p>
          <v-container grid-list-md class="pa-0">
            <v-layout wrap>
              <v-flex xs12>
                <v-select v-model="paymentForm.currencyId" :items="currencyOptions" label="Para Birimi" outlined dense></v-select>
              </v-flex>
              <v-flex xs12>
                <v-text-field v-model.number="paymentForm.amount" label="Tutar" type="number" step="0.01" min="0" outlined dense></v-text-field>
              </v-flex>
              <v-flex xs12>
                <v-select v-model="paymentForm.paymentType" :items="paymentTypeOptions" label="Ödeme Tipi" outlined dense></v-select>
              </v-flex>
              <v-flex xs12 v-if="paymentForm.paymentType === 11">
                <v-text-field v-model="paymentForm.paymentTypeOtherText" label="Diğer Açıklama" outlined dense></v-text-field>
              </v-flex>
              <v-flex xs12>
                <v-menu v-model="paymentDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="290px">
                  <template v-slot:activator="{ on }">
                    <v-text-field :value="paymentForm.date ? formatDate(paymentForm.date) : ''" label="Tarih (isteğe bağlı)" outlined dense readonly clearable prepend-inner-icon="event" v-on="on" @click:clear="paymentForm.date = null"></v-text-field>
                  </template>
                  <v-date-picker v-model="paymentForm.date" locale="tr-TR" @input="paymentDateMenu = false"></v-date-picker>
                </v-menu>
              </v-flex>
              <v-flex xs12>
                <v-textarea v-model="paymentForm.description" label="Açıklama" rows="2" outlined dense></v-textarea>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="paymentDialog = false" :disabled="paymentSaving">İptal</v-btn>
          <v-btn color="indigo" depressed class="white--text" :loading="paymentSaving" @click="submitPayment">Kaydet</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="transferDialog" max-width="520px" persistent>
      <v-card class="cash-modal">
        <v-card-title class="cash-modal-head cash-modal-head-blue">
          <v-icon left color="white">swap_horiz</v-icon>
          <span class="font-weight-bold">Transfer</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="transferDialog = false" :disabled="transferSaving"><v-icon>close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pa-4">
          <div class="cash-transfer-from">
            <span class="cash-transfer-from-label">Gönderen</span>
            <span class="cash-transfer-from-name">{{ selectedAccount ? selectedAccount.name : '' }}</span>
          </div>
          <p v-if="!currencyOptions.length" class="cash-currency-hint">Önce Admin &gt; Para Birimleri bölümünden para birimi ekleyin.</p>
          <v-container grid-list-md class="pa-0">
            <v-layout wrap>
              <v-flex xs12>
                <v-select v-model="transferForm.toAccountId" :items="transferTargetOptions" label="Hedef Hesap" outlined dense></v-select>
              </v-flex>
              <v-flex xs12>
                <v-select v-model="transferForm.currencyId" :items="currencyOptions" label="Para Birimi" outlined dense></v-select>
              </v-flex>
              <v-flex xs12 sm6 class="pr-sm-2">
                <v-text-field v-model.number="transferForm.amount" label="Tutar" type="number" step="0.01" min="0" outlined dense></v-text-field>
              </v-flex>
              <v-flex xs12 sm6 class="pl-sm-2">
                <v-text-field v-model.number="transferForm.transferFee" label="Transfer Ücreti" type="number" step="0.01" min="0" outlined dense></v-text-field>
              </v-flex>
              <v-flex xs12>
                <v-menu v-model="transferDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="290px">
                  <template v-slot:activator="{ on }">
                    <v-text-field :value="transferForm.date ? formatDate(transferForm.date) : ''" label="Tarih (isteğe bağlı)" outlined dense readonly clearable prepend-inner-icon="event" v-on="on" @click:clear="transferForm.date = null"></v-text-field>
                  </template>
                  <v-date-picker v-model="transferForm.date" locale="tr-TR" @input="transferDateMenu = false"></v-date-picker>
                </v-menu>
              </v-flex>
              <v-flex xs12>
                <v-textarea v-model="transferForm.description" label="Açıklama" rows="2" outlined dense></v-textarea>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="transferDialog = false" :disabled="transferSaving">İptal</v-btn>
          <v-btn color="primary" depressed :loading="transferSaving" @click="submitTransfer">Kaydet</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import moment from 'moment';

export default {
  data() {
    return {
      loading: false,
      accounts: [],
      currencies: [],
      selectedAccount: null,
      companyId: null,
      companyName: null,

      historyTab: 0,
      historyLoading: false,
      historyPaymentType: null,
      history: [],
      historyDate: null,
      historyDateMenu: false,
      historyRowsPerPage: 10,
      historyPage: 1,

      snapshots: [],
      snapshotsLoading: false,
      snapStartDate: null,
      snapEndDate: null,
      snapStartMenu: false,
      snapEndMenu: false,
      snapshotsRowsPerPage: 10,
      snapshotsPage: 1,
      expandedSnapshot: null,

      incomeDialog: false,
      incomeSaving: false,
      incomeDateMenu: false,
      incomeForm: { currencyId: null, amount: null, description: '', date: null },

      paymentDialog: false,
      paymentSaving: false,
      paymentDateMenu: false,
      paymentForm: { currencyId: null, amount: null, paymentType: null, paymentTypeOtherText: '', description: '', date: null },

      transferDialog: false,
      transferSaving: false,
      transferDateMenu: false,
      transferForm: { toAccountId: null, currencyId: null, amount: null, transferFee: 0, description: '', date: null },

      paymentTypeOptions: [
        { value: 0, text: 'SGK' },
        { value: 1, text: 'Banka Kredisi' },
        { value: 2, text: 'Maaşlar' },
        { value: 3, text: 'Gönderim Ücreti' },
        { value: 4, text: 'KDV' },
        { value: 5, text: 'Uyumsoft' },
        { value: 6, text: 'Kredi Kartı' },
        { value: 7, text: 'Ek Hesap İşletim Ücreti' },
        { value: 8, text: 'Onur Bey' },
        { value: 9, text: 'İdia' },
        { value: 10, text: 'Goldvion' },
        { value: 11, text: 'Diğer' },
      ],
    };
  },
  computed: {
    currencyOptions() {
      return (this.currencies || []).map((c) => ({
        value: c.id,
        text: `${c.code} - ${c.name}`,
      }));
    },
    currencyTotals() {
      return this.aggregateBalances(this.accounts || []);
    },
    bankTotals() {
      return this.aggregateBalances((this.accounts || []).filter((a) => a.accountType !== 1));
    },
    vaultTotals() {
      return this.aggregateBalances((this.accounts || []).filter((a) => a.accountType === 1));
    },
    transferTargetOptions() {
      const fromId = this.selectedAccount ? this.selectedAccount.id : null;
      return (this.accounts || [])
        .filter((a) => a && a.id !== fromId)
        .map((a) => ({ value: a.id, text: `${a.name}${a.accountType === 1 ? ' (Kasa)' : ''}` }));
    },
    historyPaymentTypeOptions() {
      return [{ value: null, text: 'Tüm Tipler' }].concat(this.paymentTypeOptions);
    },
    filteredHistory() {
      const list = this.history || [];
      if (this.historyTab !== 2 || this.historyPaymentType === null || this.historyPaymentType === undefined) return list;
      const selected = Number(this.historyPaymentType);
      const opt = this.paymentTypeOptions.find((o) => o.value === selected);
      const label = opt ? opt.text : null;
      const knownTexts = this.paymentTypeOptions.filter((o) => o.value !== 11).map((o) => o.text);
      return list.filter((row) => {
        const obj = this.getMessageObject(row.message);
        if (!obj) return false;
        const type = this.pick(obj, 'PaymentType', 'paymentType');
        if (type === undefined || type === null || type === '') return false;
        if (!Number.isNaN(Number(type))) return Number(type) === selected;
        if (selected === 11) return knownTexts.indexOf(String(type)) === -1;
        return String(type) === label;
      });
    },
    paginatedHistory() {
      const list = this.filteredHistory;
      const per = this.historyRowsPerPage || 10;
      const totalP = Math.max(1, Math.ceil(list.length / per));
      const page = Math.min(totalP, Math.max(1, this.historyPage || 1));
      const start = (page - 1) * per;
      return list.slice(start, start + per);
    },
    historyTotalPages() {
      const list = this.filteredHistory;
      const per = this.historyRowsPerPage || 10;
      return Math.max(1, Math.ceil(list.length / per));
    },
    historyPaginationInfo() {
      const list = this.filteredHistory;
      const per = this.historyRowsPerPage || 10;
      const totalP = Math.max(1, Math.ceil(list.length / per));
      const page = Math.min(totalP, Math.max(1, this.historyPage || 1));
      const start = list.length === 0 ? 0 : (page - 1) * per + 1;
      const end = Math.min(page * per, list.length);
      const total = list.length;
      if (total === 0) return '0-0 / 0';
      return `${start}-${end} / ${total}`;
    },
    paginatedSnapshots() {
      const list = this.snapshots || [];
      const per = this.snapshotsRowsPerPage || 10;
      const totalP = Math.max(1, Math.ceil(list.length / per));
      const page = Math.min(totalP, Math.max(1, this.snapshotsPage || 1));
      const start = (page - 1) * per;
      return list.slice(start, start + per);
    },
    snapshotsTotalPages() {
      const list = this.snapshots || [];
      const per = this.snapshotsRowsPerPage || 10;
      return Math.max(1, Math.ceil(list.length / per));
    },
    snapshotsPaginationInfo() {
      const list = this.snapshots || [];
      const per = this.snapshotsRowsPerPage || 10;
      const totalP = Math.max(1, Math.ceil(list.length / per));
      const page = Math.min(totalP, Math.max(1, this.snapshotsPage || 1));
      const start = list.length === 0 ? 0 : (page - 1) * per + 1;
      const end = Math.min(page * per, list.length);
      const total = list.length;
      if (total === 0) return '0-0 / 0';
      return `${start}-${end} / ${total}`;
    },
  },
  methods: {
    defaultCurrencyId() {
      const list = this.currencyOptions || [];
      return list.length ? list[0].value : null;
    },
    formatTl(value) {
      const num = Number(value);
      if (!Number.isFinite(num)) return '0,00 ₺';
      return `${num.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`;
    },
    formatMoneyCur(value, symbolOrCode) {
      const num = Number(value);
      const amount = Number.isFinite(num) ? num : 0;
      const formatted = amount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const sym = symbolOrCode ? String(symbolOrCode) : '';
      return sym ? `${sym} ${formatted}` : formatted;
    },
    aggregateBalances(list) {
      const map = {};
      (list || []).forEach((a) => {
        (a.balances || []).forEach((b) => {
          const key = b.currencyId;
          if (!map[key]) {
            map[key] = { currencyId: b.currencyId, code: b.code, name: b.name, symbol: b.symbol, total: 0 };
          }
          map[key].total += Number(b.balance) || 0;
        });
      });
      return Object.keys(map).map((k) => map[k]);
    },
    formatDate(date) {
      return date ? moment(date).format('DD.MM.YYYY') : '-';
    },
    formatDateTime(date) {
      return date ? moment(date).format('DD.MM.YYYY HH:mm') : '-';
    },
    showMessage(message) {
      this.$store.state.message = message;
      this.$store.state.dialog = true;
    },
    showError(error, fallback) {
      let message = fallback || 'İşlem sırasında bir hata oluştu.';
      if (error && error.response && error.response.data && error.response.data.message) {
        message = error.response.data.message;
      }
      this.showMessage(message);
    },
    async loadAccounts() {
      this.loading = true;
      try {
        const ctx = await this.$store.dispatch('cashAccounts/myCashAccounts');
        this.companyId = ctx.companyId !== undefined ? ctx.companyId : null;
        this.companyName = ctx.companyName || null;
        this.accounts = Array.isArray(ctx.accounts) ? ctx.accounts : [];
      } catch (e) {
        this.accounts = [];
        this.showError(e, 'Hesaplar yüklenemedi.');
      } finally {
        this.loading = false;
      }
    },
    async loadCurrencies() {
      try {
        const list = await this.$store.dispatch('cashAccounts/listCurrencies', true);
        this.currencies = Array.isArray(list) ? list : [];
      } catch (e) {
        this.currencies = [];
        this.showError(e, 'Para birimleri yüklenemedi.');
      }
    },
    onHistoryTabChanged() {
      this.historyPage = 1;
      this.historyPaymentType = null;
      this.loadHistory();
    },
    tabSubType() {
      switch (this.historyTab) {
        case 1: return 601;
        case 2: return 602;
        case 3: return 603;
        case 4: return 604;
        default: return undefined;
      }
    },
    async loadHistory() {
      this.historyLoading = true;
      try {
        const payload = {};
        const subType = this.tabSubType();
        if (subType !== undefined) payload.subType = subType;
        if (this.historyDate) payload.date = this.historyDate;
        const list = await this.$store.dispatch('cashAccounts/myCashHistory', payload);
        this.history = Array.isArray(list) ? list : [];
        this.historyPage = 1;
      } catch (e) {
        this.history = [];
        this.showError(e, 'Geçmiş yüklenemedi.');
      } finally {
        this.historyLoading = false;
      }
    },
    async refreshAll() {
      await Promise.all([this.loadAccounts(), this.loadHistory()]);
    },
    initSnapshotRange() {
      const now = new Date();
      const first = new Date(now.getFullYear(), now.getMonth(), 1);
      this.snapStartDate = moment(first).format('YYYY-MM-DD');
      this.snapEndDate = moment(now).format('YYYY-MM-DD');
    },
    snapPick(obj, pascal, camel) {
      return this.pick(obj, pascal, camel);
    },
    snapshotData(item) {
      if (!item) return {};
      if (item._parsed) return item._parsed;
      const obj = this.getMessageObject(item.message) || {};
      item._parsed = obj;
      return obj;
    },
    snapshotTotals(item) {
      const totals = {};
      this.snapshotAccounts(item).forEach((acc) => {
        this.snapshotAccountBalances(acc).forEach((b) => {
          const code = this.pick(b, 'Code', 'code') || '?';
          if (!totals[code]) {
            totals[code] = { code, symbol: this.pick(b, 'Symbol', 'symbol'), total: 0 };
          }
          totals[code].total += Number(this.pick(b, 'Balance', 'balance')) || 0;
        });
      });
      return Object.values(totals);
    },
    snapshotAccounts(item) {
      const data = this.snapshotData(item);
      const list = this.pick(data, 'Accounts', 'accounts');
      if (!Array.isArray(list)) return [];
      return list.filter((acc) => {
        if (Number(this.pick(acc, 'AccountType', 'accountType')) === 1) return true;
        if (this.companyId === null || this.companyId === undefined) return false;
        return Number(this.pick(acc, 'CompanyId', 'companyId')) === Number(this.companyId);
      });
    },
    snapshotAccountBalances(acc) {
      const list = this.pick(acc, 'Balances', 'balances');
      return Array.isArray(list) ? list : [];
    },
    toggleSnapshotDetail(i) {
      this.expandedSnapshot = this.expandedSnapshot === i ? null : i;
    },
    async loadSnapshots() {
      this.snapshotsLoading = true;
      this.expandedSnapshot = null;
      try {
        const list = await this.$store.dispatch('cashAccounts/cashSnapshots', {
          startDate: this.snapStartDate,
          endDate: this.snapEndDate,
        });
        this.snapshots = Array.isArray(list) ? list : [];
        this.snapshotsPage = 1;
      } catch (e) {
        this.snapshots = [];
        this.showError(e, 'Snapshot kayıtları yüklenemedi.');
      } finally {
        this.snapshotsLoading = false;
      }
    },
    async exportSnapshotsExcel() {
      const XLSX = require('xlsx');
      let rows;
      try {
        rows = await this.$store.dispatch('cashAccounts/cashSnapshots', {
          startDate: this.snapStartDate,
          endDate: this.snapEndDate,
        });
      } catch (e) {
        this.showError(e, 'Snapshot kayıtları yüklenemedi.');
        return;
      }
      rows = Array.isArray(rows) ? rows : [];
      if (!rows.length) {
        this.$store.state.message = 'Seçilen aralıkta snapshot bulunamadı.';
        this.$store.state.dialog = true;
        return;
      }
      rows.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      const wb = XLSX.utils.book_new();
      const usedNames = {};
      rows.forEach((item) => {
        const totalsList = this.snapshotTotals(item);
        const accountsList = this.snapshotAccounts(item);
        const aoa = [];
        aoa.push(['Tarih', this.formatDateTime(item.createdAt)]);
        aoa.push([]);
        aoa.push(['TOPLAMLAR']);
        aoa.push(['Para Birimi', 'Toplam']);
        totalsList.forEach((t) => {
          const label = this.pick(t, 'Code', 'code') || this.pick(t, 'Symbol', 'symbol') || '';
          aoa.push([label, Number(this.pick(t, 'Total', 'total')) || 0]);
        });
        aoa.push([]);
        aoa.push(['HESAPLAR']);
        aoa.push(['Hesap', 'Tip', 'Durum', 'Para Birimi', 'Bakiye']);
        accountsList.forEach((acc) => {
          const name = this.pick(acc, 'Name', 'name') || '';
          const type = Number(this.pick(acc, 'AccountType', 'accountType')) === 1 ? 'Kasa' : 'Banka';
          const status = this.pick(acc, 'Status', 'status') === 'ACTIVE' ? 'Aktif' : 'Pasif';
          const balances = this.pick(acc, 'Balances', 'balances');
          const balList = Array.isArray(balances) ? balances : [];
          if (!balList.length) {
            aoa.push([name, type, status, '-', 0]);
          } else {
            balList.forEach((b) => {
              const curLabel = this.pick(b, 'Code', 'code') || this.pick(b, 'Symbol', 'symbol') || '';
              aoa.push([name, type, status, curLabel, Number(this.pick(b, 'Balance', 'balance')) || 0]);
            });
          }
        });
        const ws = XLSX.utils.aoa_to_sheet(aoa);
        let baseName = String(this.formatDate(item.createdAt) || 'Snapshot').replace(/[\\/?*[\]:]/g, '.');
        if (baseName.length > 31) baseName = baseName.slice(0, 31);
        let sheetName = baseName;
        if (usedNames[sheetName]) {
          usedNames[baseName] += 1;
          const suffix = ' (' + usedNames[baseName] + ')';
          sheetName = baseName.slice(0, 31 - suffix.length) + suffix;
        } else {
          usedNames[sheetName] = 1;
        }
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
      });
      XLSX.writeFile(wb, 'ParaBanka_Snapshot_' + this.snapStartDate + '_' + this.snapEndDate + '.xlsx');
    },
    getMessageObject(message) {
      if (!message) return null;
      if (typeof message === 'object') return message;
      const raw = String(message).trim();
      if (!raw) return null;
      if (raw.startsWith('{') && raw.endsWith('}')) {
        try {
          return JSON.parse(raw);
        } catch (_e) {
          return null;
        }
      }
      return null;
    },
    pick(obj, pascal, camel) {
      if (!obj) return undefined;
      if (obj[pascal] !== undefined && obj[pascal] !== null) return obj[pascal];
      if (obj[camel] !== undefined && obj[camel] !== null) return obj[camel];
      return undefined;
    },
    pickBalance(node) {
      if (node === undefined || node === null) return undefined;
      if (typeof node === 'object') return this.pick(node, 'Balance', 'balance');
      return node;
    },
    historyTypeLabel(subType) {
      switch (Number(subType)) {
        case 601: return 'Para Girdisi';
        case 602: return 'Diğer Ödeme';
        case 603: return 'Transfer';
        case 604: return 'Düzeltme';
        case 600: return 'Hesap İşlemi';
        default: return 'Hareket';
      }
    },
    historyChipClass(subType) {
      switch (Number(subType)) {
        case 601: return 'cash-chip-green';
        case 602: return 'cash-chip-indigo';
        case 603: return 'cash-chip-blue';
        case 604: return 'cash-chip-slate';
        default: return 'cash-chip-grey';
      }
    },
    historyAmountClass(subType) {
      switch (Number(subType)) {
        case 601: return 'cash-amount-positive';
        case 602: return 'cash-amount-negative';
        default: return '';
      }
    },
    historyAccountLabel(row) {
      const obj = this.getMessageObject(row.message);
      if (!obj) return '-';
      if (Number(row.subType) === 603) {
        const from = this.pick(obj, 'FromAccountName', 'fromAccountName') || '?';
        const to = this.pick(obj, 'ToAccountName', 'toAccountName') || '?';
        return `${from} → ${to}`;
      }
      return this.pick(obj, 'AccountName', 'accountName') || '-';
    },
    historyAmount(row) {
      const obj = this.getMessageObject(row.message);
      if (!obj) return '-';
      let value;
      if (Number(row.subType) === 604) {
        value = this.pick(obj, 'Delta', 'delta');
      } else {
        value = this.pick(obj, 'Amount', 'amount');
      }
      if (value === undefined || value === null) return '-';
      return this.formatMoneyCur(value, this.historyCurSymbol(row));
    },
    historyCurrency(row) {
      const obj = this.getMessageObject(row.message);
      if (!obj) return '';
      const cur = this.pick(obj, 'Currency', 'currency');
      return cur ? String(cur) : '';
    },
    historyCurSymbol(row) {
      const code = this.historyCurrency(row);
      if (!code) return '₺';
      const found = (this.currencies || []).find((c) => c.code === code);
      return (found && found.symbol) || code;
    },
    historyAfterBalance(row) {
      const obj = this.getMessageObject(row.message);
      if (!obj) return '-';
      if (Number(row.subType) === 603) {
        const fromAfter = this.pickBalance(this.pick(obj, 'FromAfter', 'fromAfter'));
        const toAfter = this.pickBalance(this.pick(obj, 'ToAfter', 'toAfter'));
        const parts = [];
        if (fromAfter !== undefined) parts.push(this.formatMoneyCur(fromAfter, this.historyCurSymbol(row)));
        if (toAfter !== undefined) parts.push(this.formatMoneyCur(toAfter, this.historyCurSymbol(row)));
        return parts.length ? parts.join(' / ') : '-';
      }
      const after = this.pickBalance(this.pick(obj, 'After', 'after'));
      return after === undefined ? '-' : this.formatMoneyCur(after, this.historyCurSymbol(row));
    },
    historyDescription(row) {
      const obj = this.getMessageObject(row.message);
      if (!obj) return '-';
      const parts = [];
      if (Number(row.subType) === 602) {
        const type = this.pick(obj, 'PaymentType', 'paymentType');
        if (type !== undefined && type !== '') parts.push(this.paymentTypeText(type));
      }
      if (Number(row.subType) === 600) {
        const action = this.pick(obj, 'Action', 'action');
        if (action) parts.push(this.accountActionText(action));
      }
      if (Number(row.subType) === 603) {
        const fee = this.pick(obj, 'TransferFee', 'transferFee');
        if (fee !== undefined && Number(fee) > 0) parts.push(`Ücret: ${this.formatMoneyCur(fee, this.historyCurSymbol(row))}`);
      }
      const desc = this.pick(obj, 'Description', 'description');
      if (desc) parts.push(String(desc));
      return parts.length ? parts.join(' — ') : '-';
    },
    paymentTypeText(type) {
      const found = this.paymentTypeOptions.find((o) => o.value === Number(type));
      if (found) return found.text;
      return String(type);
    },
    accountActionText(action) {
      const a = String(action).toLowerCase();
      if (a === 'create') return 'Hesap Oluşturma';
      if (a === 'update') return 'Hesap Güncelleme';
      if (a === 'delete') return 'Hesap Silme';
      return String(action);
    },
    openIncomeDialog(acc) {
      this.selectedAccount = acc;
      this.incomeForm = { currencyId: this.defaultCurrencyId(), amount: null, description: '', date: null };
      this.incomeDialog = true;
    },
    async submitIncome() {
      if (!this.selectedAccount) return;
      if (this.incomeForm.currencyId === null || this.incomeForm.currencyId === undefined) {
        this.showMessage('Lütfen para birimini seçin.');
        return;
      }
      if (this.incomeForm.amount === null || this.incomeForm.amount === undefined || Number(this.incomeForm.amount) <= 0) {
        this.showMessage('Lütfen geçerli bir tutar girin.');
        return;
      }
      this.incomeSaving = true;
      try {
        await this.$store.dispatch('cashAccounts/cashAddIncome', {
          accountId: this.selectedAccount.id,
          currencyId: this.incomeForm.currencyId,
          amount: Number(this.incomeForm.amount),
          description: this.incomeForm.description || '',
          date: this.incomeForm.date || null,
        });
        this.incomeDialog = false;
        await this.refreshAll();
        this.showMessage('Para girdisi kaydedildi.');
      } catch (e) {
        this.showError(e, 'Para girdisi kaydedilemedi.');
      } finally {
        this.incomeSaving = false;
      }
    },
    openPaymentDialog(acc) {
      this.selectedAccount = acc;
      this.paymentForm = { currencyId: this.defaultCurrencyId(), amount: null, paymentType: null, paymentTypeOtherText: '', description: '', date: null };
      this.paymentDialog = true;
    },
    async submitPayment() {
      if (!this.selectedAccount) return;
      if (this.paymentForm.currencyId === null || this.paymentForm.currencyId === undefined) {
        this.showMessage('Lütfen para birimini seçin.');
        return;
      }
      if (this.paymentForm.amount === null || this.paymentForm.amount === undefined || Number(this.paymentForm.amount) <= 0) {
        this.showMessage('Lütfen geçerli bir tutar girin.');
        return;
      }
      if (this.paymentForm.paymentType === null || this.paymentForm.paymentType === undefined) {
        this.showMessage('Lütfen ödeme tipini seçin.');
        return;
      }
      if (this.paymentForm.paymentType === 11 && (!this.paymentForm.paymentTypeOtherText || !String(this.paymentForm.paymentTypeOtherText).trim())) {
        this.showMessage('Lütfen "Diğer" için açıklama girin.');
        return;
      }
      this.paymentSaving = true;
      try {
        await this.$store.dispatch('cashAccounts/cashExternalPayment', {
          accountId: this.selectedAccount.id,
          currencyId: this.paymentForm.currencyId,
          amount: Number(this.paymentForm.amount),
          paymentType: this.paymentForm.paymentType,
          paymentTypeOtherText: this.paymentForm.paymentType === 11 ? String(this.paymentForm.paymentTypeOtherText).trim() : '',
          description: this.paymentForm.description || '',
          receiptUrl: '',
          date: this.paymentForm.date || null,
        });
        this.paymentDialog = false;
        await this.refreshAll();
        this.showMessage('Ödeme kaydedildi.');
      } catch (e) {
        this.showError(e, 'Ödeme kaydedilemedi.');
      } finally {
        this.paymentSaving = false;
      }
    },
    openTransferDialog(acc) {
      this.selectedAccount = acc;
      this.transferForm = { toAccountId: null, amount: null, transferFee: 0, description: '', date: null };
      this.transferDialog = true;
    },
    async submitTransfer() {
      if (!this.selectedAccount) return;
      if (!this.transferForm.toAccountId) {
        this.showMessage('Lütfen hedef hesabı seçin.');
        return;
      }
      if (this.transferForm.currencyId === null || this.transferForm.currencyId === undefined) {
        this.showMessage('Lütfen para birimini seçin.');
        return;
      }
      if (this.transferForm.amount === null || this.transferForm.amount === undefined || Number(this.transferForm.amount) <= 0) {
        this.showMessage('Lütfen geçerli bir tutar girin.');
        return;
      }
      this.transferSaving = true;
      try {
        await this.$store.dispatch('cashAccounts/cashTransfer', {
          fromAccountId: this.selectedAccount.id,
          toAccountId: this.transferForm.toAccountId,
          currencyId: this.transferForm.currencyId,
          amount: Number(this.transferForm.amount),
          transferFee: Number(this.transferForm.transferFee) || 0,
          description: this.transferForm.description || '',
          receiptUrl: '',
          date: this.transferForm.date || null,
        });
        this.transferDialog = false;
        await this.refreshAll();
        this.showMessage('Transfer tamamlandı.');
      } catch (e) {
        this.showError(e, 'Transfer yapılamadı.');
      } finally {
        this.transferSaving = false;
      }
    },
  },
  async mounted() {
    this.initSnapshotRange();
    await Promise.all([this.refreshAll(), this.loadCurrencies()]);
    this.loadSnapshots();
  },
};
</script>

<style scoped>
.cash-page {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

.cash-shell {
  max-width: 80rem;
  margin: 0 auto;
  padding: 1.75rem 1.5rem 2.5rem;
}

.cash-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.cash-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  letter-spacing: -0.025em;
}

.cash-subtitle {
  font-size: 0.875rem;
  color: #64748b;
  margin: 0.25rem 0 0 0;
}

.cash-header-actions {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}

.cash-company-filter {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.cash-company-label {
  font-size: 0.75rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.cash-company-select {
  min-width: 200px;
}

.cash-company-select >>> .v-input__slot {
  margin-bottom: 0;
}

.cash-company-chip {
  font-size: 0.6875rem;
  font-weight: 700;
  color: #2563eb;
  background: rgba(37, 99, 235, 0.1);
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  white-space: nowrap;
}

.cash-btn {
  display: inline-flex;
  align-items: center;
  padding: 0.55rem 1.25rem;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 0.875rem;
  border: none;
  cursor: pointer;
  transition: background 0.2s, box-shadow 0.2s, border-color 0.2s;
}

.cash-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.cash-btn-primary {
  background: #2563eb;
  color: #fff;
  box-shadow: 0 1px 2px rgba(37, 99, 235, 0.2);
}

.cash-btn-primary:hover:not(:disabled) {
  background: #1d4ed8;
}

.cash-btn-ghost {
  background: #fff;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.cash-btn-ghost:hover:not(:disabled) {
  border-color: #2563eb;
  background: #f8fafc;
}

.cash-summary {
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 1rem;
  margin-bottom: 1.75rem;
}

@media (min-width: 700px) {
  .cash-summary {
    grid-template-columns: repeat(3, 1fr);
  }
}

.cash-summary-card {
  background: #fff;
  padding: 1.25rem;
  border-radius: 0.75rem;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.cash-summary-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;
}

.cash-summary-label {
  font-size: 0.6875rem;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.cash-summary-icon {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cash-icon-primary {
  background: rgba(37, 99, 235, 0.1);
  color: #2563eb;
}

.cash-icon-blue {
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
}

.cash-icon-amber {
  background: rgba(245, 158, 11, 0.12);
  color: #d97706;
}

.cash-summary-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.025em;
}

.cash-negative {
  color: #dc2626 !important;
}

.cash-history-filter {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.75rem 1.5rem 0;
}

.cash-history-filter-select {
  max-width: 240px;
}

.cash-summary-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.25rem;
}

.cash-summary-chip {
  display: inline-flex;
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 0.3rem 0.65rem;
  font-size: 0.9375rem;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.01em;
  font-variant-numeric: tabular-nums;
}

.cash-summary-empty {
  font-size: 0.875rem;
  color: #94a3b8;
  padding: 0.4rem 0;
}

.cash-card-balances {
  margin: 0.85rem 0 1rem;
  display: flex;
  flex-direction: column;
}

.cash-card-balance-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.45rem 0;
  border-bottom: 1px dashed #e2e8f0;
}

.cash-card-balance-line:last-child {
  border-bottom: none;
}

.cash-bal-code {
  font-size: 0.6875rem;
  font-weight: 700;
  color: #64748b;
  background: #f1f5f9;
  border-radius: 999px;
  padding: 0.14rem 0.55rem;
  letter-spacing: 0.05em;
}

.cash-bal-amount {
  font-size: 1.0625rem;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
}

.cash-card-balance-empty {
  font-size: 0.875rem;
  color: #94a3b8;
  padding: 0.6rem 0;
}

.cash-company-chip {
  font-size: 0.6875rem;
  font-weight: 700;
  color: #2563eb;
  background: rgba(37, 99, 235, 0.08);
  border-radius: 999px;
  padding: 0.15rem 0.55rem;
}

.cash-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 3.5rem 1rem;
  color: #64748b;
  font-size: 0.9375rem;
}

.cash-empty {
  background: #fff;
  border: 1px dashed #cbd5e1;
  border-radius: 0.75rem;
}

.cash-grid {
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 1rem;
  margin-bottom: 2.25rem;
}

@media (min-width: 640px) {
  .cash-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1024px) {
  .cash-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

.cash-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0.875rem;
  padding: 1.15rem 1.25rem 1.25rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.2s, transform 0.2s;
}

.cash-card:hover {
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.09);
}

.cash-card-vault {
  border-color: #fcd9a4;
  background: linear-gradient(180deg, #fffdf7 0%, #ffffff 55%);
}

.cash-card-passive {
  opacity: 0.62;
}

.cash-card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.85rem;
}

.cash-card-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.cash-badge-bank {
  background: rgba(59, 130, 246, 0.12);
  color: #2563eb;
}

.cash-badge-vault {
  background: rgba(245, 158, 11, 0.16);
  color: #b45309;
}

.cash-card-menu {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.cash-pill-passive {
  font-size: 0.6875rem;
  font-weight: 700;
  color: #64748b;
  background: #f1f5f9;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
}

.cash-icon-btn {
  border: none;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  border-radius: 0.375rem;
  padding: 0.2rem;
  display: inline-flex;
  align-items: center;
}

.cash-icon-btn:hover {
  background: #f1f5f9;
  color: #0f172a;
}

.cash-card-name {
  font-size: 1.0625rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.2;
}

.cash-card-iban {
  font-size: 0.75rem;
  color: #94a3b8;
  margin-top: 0.25rem;
  letter-spacing: 0.02em;
  word-break: break-all;
}

.cash-card-balance {
  font-size: 1.625rem;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.03em;
  margin: 0.75rem 0 1.05rem;
}

.cash-card-actions {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.5rem;
  margin-top: auto;
  padding-top: 0.25rem;
}

.cash-op-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.45rem 0.25rem;
  border: none;
  border-radius: 0.5rem;
  font-size: 0.72rem;
  font-weight: 700;
  color: #fff;
  cursor: pointer;
  white-space: nowrap;
  transition: filter 0.2s;
}

.cash-op-btn:hover {
  filter: brightness(1.06);
}

.cash-op-income {
  background: #059669;
}

.cash-op-payment {
  background: #4f46e5;
}

.cash-op-transfer {
  background: #2563eb;
}

.cash-menu-card {
  border-radius: 0.625rem;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.14);
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

.cash-menu-body {
  padding: 0.375rem;
  min-width: 190px;
}

.cash-menu-item {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.55rem 0.75rem;
  border: none;
  background: transparent;
  border-radius: 0.5rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #334155;
  cursor: pointer;
  text-align: left;
}

.cash-menu-item:hover {
  background: #f1f5f9;
}

.cash-menu-danger {
  color: #dc2626;
}

.cash-menu-danger:hover {
  background: rgba(220, 38, 38, 0.08);
}

.cash-menu-divider {
  height: 1px;
  background: #e2e8f0;
  margin: 0.375rem 0.25rem;
}

.cash-history {
  margin-top: 0.5rem;
}

.cash-history-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.cash-history-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  letter-spacing: -0.02em;
}

.cash-history-tabs {
  margin-bottom: 1rem;
}

.cash-history-tabs >>> .v-tabs__bar {
  background: transparent !important;
}

.cash-history-tabs >>> .v-tabs__item {
  color: #64748b !important;
  font-weight: 700;
  text-transform: none;
  letter-spacing: 0.01em;
}

.cash-history-tabs >>> .v-tabs__item--active {
  color: #2563eb !important;
}

.cash-table-wrap {
  background: #fff;
  border-radius: 0.625rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

.cash-table-scroll {
  overflow-x: auto;
}

.cash-table {
  width: 100%;
  text-align: left;
  border-collapse: collapse;
}

.cash-table thead tr {
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.cash-table th {
  padding: 0.85rem 1.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;
}

.cash-table tbody tr {
  border-bottom: 1px solid #f1f5f9;
  transition: background 0.15s;
}

.cash-table tbody tr:hover {
  background: rgba(248, 250, 252, 0.7);
}

.cash-table td {
  padding: 0.85rem 1.25rem;
  font-size: 0.875rem;
  color: #475569;
}

.cash-cell-state {
  text-align: center;
  padding: 2rem !important;
  color: #64748b;
}

.cash-cell-bold {
  font-weight: 600;
  color: #1e293b;
}

.cash-cell-muted {
  color: #94a3b8;
  white-space: nowrap;
}

.cash-cell-value {
  font-weight: 500;
  color: #1e293b;
  white-space: nowrap;
}

.cash-cell-amount {
  font-weight: 700;
  color: #1e293b;
  white-space: nowrap;
}

.cash-amount-positive {
  color: #059669;
}

.cash-amount-negative {
  color: #dc2626;
}

.cash-cell-desc {
  max-width: 320px;
}

.cash-chip {
  display: inline-block;
  padding: 0.2rem 0.55rem;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}

.cash-chip-green {
  background: rgba(16, 185, 129, 0.14);
  color: #059669;
}

.cash-chip-indigo {
  background: rgba(79, 70, 229, 0.14);
  color: #4f46e5;
}

.cash-chip-blue {
  background: rgba(37, 99, 235, 0.14);
  color: #2563eb;
}

.cash-chip-slate {
  background: rgba(100, 116, 139, 0.14);
  color: #475569;
}

.cash-chip-grey {
  background: #f1f5f9;
  color: #64748b;
}

.cash-table-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 1.5rem;
  padding: 0.85rem 1.25rem;
  background: rgba(248, 250, 252, 0.6);
  font-size: 0.875rem;
  color: #64748b;
  flex-wrap: wrap;
}

.cash-footer-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.cash-rows-select {
  background: transparent;
  border: none;
  color: #0f172a;
  cursor: pointer;
  font-weight: 500;
  padding: 0.25rem 0;
}

.cash-rows-select:focus {
  outline: none;
}

.cash-pagination-info {
  margin-left: auto;
}

.cash-footer-actions {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.cash-page-btn {
  padding: 0.25rem;
  border: none;
  background: transparent;
  border-radius: 0.25rem;
  cursor: pointer;
  color: #64748b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.cash-page-btn:hover:not(:disabled) {
  background: #e2e8f0;
  color: #0f172a;
}

.cash-page-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.cash-modal {
  overflow: hidden;
  border-radius: 12px;
}

.cash-modal-head {
  display: flex;
  align-items: center;
  color: #fff;
  padding: 1rem 1.25rem;
}

.cash-modal-head span {
  font-size: 1.0625rem;
}

.cash-modal-head-blue {
  background: linear-gradient(135deg, #1d4ed8, #2563eb);
}

.cash-modal-head-amber {
  background: linear-gradient(135deg, #d97706, #f59e0b);
}

.cash-modal-head-green {
  background: linear-gradient(135deg, #047857, #059669);
}

.cash-modal-head-indigo {
  background: linear-gradient(135deg, #4338ca, #4f46e5);
}

.cash-modal-head-slate {
  background: linear-gradient(135deg, #475569, #64748b);
}

.cash-modal-head-red {
  background: linear-gradient(135deg, #b91c1c, #dc2626);
}

.cash-target {
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 0.5rem;
}

.cash-transfer-from {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding: 0.75rem 1rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  margin-bottom: 1rem;
}

.cash-transfer-from-label {
  font-size: 0.6875rem;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.cash-transfer-from-name {
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
}

.cash-snapshot-section {
  margin-top: 2.25rem;
}

.cash-btn-success {
  background: #059669;
  color: #fff;
  box-shadow: 0 1px 2px rgba(5, 150, 105, 0.2);
}

.cash-btn-success:hover:not(:disabled) {
  background: #047857;
}

.cash-snap-th-detail {
  width: 120px;
  text-align: center;
}

.cash-snap-cell-detail {
  text-align: center;
  width: 120px;
}

.cash-snap-detail-btn {
  flex: 0 0 auto;
  padding: 0.35rem 0.85rem;
}

.cash-cur-chip {
  display: inline-block;
  padding: 0.2rem 0.55rem;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 600;
  background: #f1f5f9;
  color: #1e293b;
  white-space: nowrap;
  margin-left: 0.35rem;
  font-variant-numeric: tabular-nums;
}

.cash-snap-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.cash-snap-chips .cash-cur-chip {
  margin-left: 0;
}

.cash-snap-detail-row td {
  background: #f8fafc;
  padding: 1rem 1.25rem;
}

.cash-snap-detail-wrap {
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 0.75rem;
}

@media (min-width: 768px) {
  .cash-snap-detail-wrap {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1200px) {
  .cash-snap-detail-wrap {
    grid-template-columns: repeat(3, 1fr);
  }
}

.cash-snap-detail-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
}

.cash-snap-detail-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.625rem;
}

.cash-snap-detail-name {
  font-weight: 700;
  color: #0f172a;
  font-size: 0.9rem;
  margin-right: 0.25rem;
}

.cash-snap-balances {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.cash-snap-balances .cash-cur-chip {
  margin-left: 0;
}

</style>
